import dayjs from 'dayjs';

import { Task, TaskMemberRole, TaskStatus } from '@graphql/generated';
import { TaskTagType } from '@src/types/task';

import type { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import type { Dayjs } from 'dayjs';

export const filterOutStatusFromTasks = (
  tasks: Task | Task[] | TaskTagType[],
  excludeStatus: TaskStatus
): Task[] | TaskTagType[] => {
  return tasks.filter((t) => t.status !== excludeStatus);
};

/**
 * Keeps the items whose date falls within [start, end], inclusive of both
 * boundary *days*.
 *
 * Compared per calendar day, not by elapsed time: `dayjs().diff(other, 'day')`
 * truncates, so a `-0.96 day` difference read as `0` and let the whole 24 hours
 * before `start` (and after `end`) through — a file uploaded June 30 at 23:00
 * showed inside a July 1–7 window, which breaks the inclusive-window rule the
 * Files & Media and Activity date filters are specified against (TA-10336).
 */
export const filterByDateRange = <T extends Record<string, unknown>>(
  tasks: T[],
  keyFunc: (obj: T) => Dayjs | string,
  { start, end }: DateRange
) => {
  if (!start && !end) return tasks;

  return tasks.filter((item) => {
    const date = dayjs(keyFunc(item));
    if (start && date.isBefore(start, 'day')) return false;
    if (end && date.isAfter(end, 'day')) return false;
    return true;
  });
};

export const selectableTaskRoles = Object.values(TaskMemberRole).filter(
  (r) => r !== TaskMemberRole.Owner
);
export const defaultTaskRole = TaskMemberRole.Assignee;
export const defaultAssignMemberTaskRole = TaskMemberRole.Assignee;
export const getTaskRoleEnumValue = (role: string) => {
  const indexOfRole = Object.values(TaskMemberRole).indexOf(
    role as TaskMemberRole
  );
  return Object.values(TaskMemberRole)[indexOfRole];
};
